import { useState, useEffect, useRef } from "react";

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    const el = ref.current;
    if (el) obs.observe(el);
    return () => { if (el) obs.unobserve(el); };
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {}, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0px)" : "translateY(15px)",
      transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

export default function Awards() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .awards-page {
          background-color: #ffffff;
          color: #1a1a1a;
          font-family: 'Plus Jakarta Sans', sans-serif;
          line-height: 1.6;
          padding-bottom: 120px;
        }

        .awards-hero {
          background: #ffffff;
          padding: 80px 20px 40px;
          text-align: center;
          border-bottom: 1px solid #eee;
        }

        .hero-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(40px, 6vw, 64px);
          font-weight: 800;
          margin-bottom: 8px;
          color: #111;
          text-transform: uppercase;
        }

        .hero-subtitle {
          font-size: clamp(16px, 2.5vw, 24px);
          font-weight: 700;
          color: #006600;
          opacity: 1;
        }

        .container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .section-title {
          font-size: 36px;
          font-weight: 800;
          color: #111;
          margin-bottom: 24px;
          text-align: left;
        }

        .intro-text p {
          font-size: 16px;
          color: #444;
          margin-bottom: 24px;
          line-height: 1.8;
          text-align: justify;
        }

        .rewards-list-section {
          margin-top: 80px;
          text-align: center;
        }

        .rewards-header {
          font-size: 40px;
          font-weight: 950;
          color: #111;
          margin-bottom: 60px;
          position: relative;
          display: inline-block;
          padding-bottom: 15px;
        }

        .rewards-header::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 4px;
          background: #006600;
          border-radius: 999px;
        }

        .reward-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 30px;
        }

        .reward-card {
          padding: 40px 30px;
          background: #fbfbfb;
          border: 1px solid #eee;
          border-radius: 20px;
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .reward-card:hover {
          transform: translateY(-10px);
          background: #fff;
          box-shadow: 0 20px 40px rgba(0,0,0,0.06);
          border-color: #006600;
        }

        .reward-icon {
          font-size: 32px;
          margin-bottom: 16px;
          background: rgba(0, 102, 0, 0.08);
          width: 70px;
          height: 70px;
          display: grid;
          place-items: center;
          margin: 0 auto 20px;
          border-radius: 50%;
          color: #006600;
        }

        .reward-name {
          font-size: 18px;
          font-weight: 800;
          margin-bottom: 12px;
          color: #111;
        }

        .reward-desc {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .section-title { font-size: 28px; }
          .awards-hero { height: 40vh; min-height: 300px; }
        }
      `}</style>

      <div className="awards-page">
        <section className="awards-hero">
          <div className="hero-content">
            <Reveal><h1 className="hero-title">SUN DISTRIBUTORS</h1></Reveal>
            <Reveal delay={0.1}><p className="hero-subtitle">Change Your Thought, Change Your Life</p></Reveal>
            <Reveal delay={0.2}><div style={{ fontSize: '20px', fontWeight: 800, color: '#f1c40f', marginTop: '10px' }}>AWARDS</div></Reveal>
          </div>
        </section>

        <div className="container">
          <div className="intro-section">
            <Reveal><h2 className="section-title">Introduction</h2></Reveal>
            <div className="intro-text">
              <Reveal delay={0.1}>
                <p>
                  At <strong>SUN DISTRIBUTORS</strong>, our primary goal is to recognize and reward the hard work of every entrepreneur in our network.
                  We believe that the more effort you put in, the greater your rewards will be.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p>
                  Every "Pair" you achieve is a symbol of your dedication, belief, and consistency.
                  As you continue to grow and achieve more pairs, you unlock bigger and better rewards that reflect your success and commitment.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p>
                  Your journey with SUN DISTRIBUTORS is not just about earning — it’s about growing, achieving, and being recognized for every milestone you reach.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="rewards-list-section">
            <Reveal delay={0.3}><h2 className="rewards-header">Rewards List</h2></Reveal>
            
            <div className="reward-grid">
              <Reveal delay={0.35}>
                <div className="reward-card">
                  <div className="reward-icon">🎖️</div>
                  <h3 className="reward-name">Achievement Milestone</h3>
                  <p className="reward-desc">Recognizing your first significant steps into the world of professional entrepreneurship.</p>
                </div>
              </Reveal>
              <Reveal delay={0.4}>
                <div className="reward-card">
                  <div className="reward-icon">🤝</div>
                  <h3 className="reward-name">Pair Excellence Reward</h3>
                  <p className="reward-desc">A special recognition for consistently maintaining balance and growth in your team network.</p>
                </div>
              </Reveal>
              <Reveal delay={0.45}>
                <div className="reward-card">
                  <div className="reward-icon">✈️</div>
                  <h3 className="reward-name">Luxury Tours</h3>
                  <p className="reward-desc">Exclusive travel experiences designed to celebrate our top-tier leaders' success and dedication.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
